import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatRupiah } from "@/lib/api";

export const COLORS = { success: "#22c55e", rejected: "#f43f5e", pending: "#f59e0b" };
export const LABEL = { success: "Sukses", rejected: "Ditolak", pending: "Pending" };
export const pct = (n, total) => (total ? Math.round((n / total) * 1000) / 10 : 0);
const TIP = { contentStyle: { background: "#15151b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }, labelStyle: { color: "#e5b80b" } };
const shortRp = (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : v);

const Card = ({ title, children, testid, className = "" }) => (
  <div className={`rk-glass rounded-2xl p-5 ${className}`} data-testid={testid}>
    <h3 className="mb-4 text-sm font-bold uppercase tracking-widest text-zinc-400">{title}</h3>
    {children}
  </div>
);

export const TrendChart = ({ data, hourly }) => {
  const rows = data.map((d) => ({ ...d, label: hourly ? `${d.slot}:00` : d.slot.slice(5).split("-").reverse().join("/"), rp: d.rp_topup + d.rp_bongkar }));
  return (
    <Card title={hourly ? "Tren per Jam (WIB)" : "Tren Harian"} testid="trend-chart" className="lg:col-span-2">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ left: -10, right: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="n" allowDecimals={false} tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="rp" orientation="right" tickFormatter={shortRp} tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip {...TIP} formatter={(v, name) => (name === "Rupiah sukses" ? formatRupiah(v) : v)} cursor={{ fill: "rgba(229,184,11,0.06)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {Object.keys(COLORS).map((k, i) => (
              <Bar key={k} yAxisId="n" dataKey={k} name={LABEL[k]} stackId="s" fill={COLORS[k]} radius={i === 2 ? [4, 4, 0, 0] : 0} maxBarSize={36} />
            ))}
            <Line yAxisId="rp" type="monotone" dataKey="rp" name="Rupiah sukses" stroke="#e5b80b" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export const StatusDonut = ({ s }) => {
  const data = Object.keys(COLORS).map((k) => ({ key: k, name: LABEL[k], value: s[k] }));
  return (
    <Card title="Persentase Status" testid="status-donut">
      <div className="relative h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={s.count ? data : [{ key: "none", value: 1 }]} dataKey="value" innerRadius={62} outerRadius={88} paddingAngle={s.count ? 3 : 0} stroke="none">
              {(s.count ? data : [{ key: "none" }]).map((d) => <Cell key={d.key} fill={COLORS[d.key] || "#27272a"} />)}
            </Pie>
            {s.count ? <Tooltip {...TIP} /> : null}
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-extrabold" data-testid="donut-success-pct">{pct(s.success, s.count)}%</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sukses</div>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5 text-sm">
        {data.map((d) => (
          <li key={d.key} className="flex items-center justify-between" data-testid={`donut-legend-${d.key}`}>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[d.key] }} />{d.name}</span>
            <span className="font-mono text-zinc-300">{d.value} · {pct(d.value, s.count)}%</span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

const BreakRow = ({ name, s, testid }) => (
  <div className="border-t border-white/5 py-3 first:border-0" data-testid={testid}>
    <div className="flex items-center justify-between text-sm">
      <span className="font-semibold">{name}</span>
      <span className="text-xs text-zinc-400">{s.count} trx · <span className="text-[#ffd700]">{formatRupiah(s.rp_success)}</span></span>
    </div>
    <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-white/5">
      {Object.keys(COLORS).map((k) => <div key={k} style={{ width: `${pct(s[k], s.count)}%`, background: COLORS[k] }} />)}
    </div>
    <div className="mt-1.5 flex gap-3 text-[11px] text-zinc-500">
      {Object.keys(COLORS).map((k) => <span key={k}>{LABEL[k]} {pct(s[k], s.count)}%</span>)}
    </div>
  </div>
);

export const Breakdown = ({ title, items, testid }) => (
  <Card title={title} testid={testid}>
    {items.length === 0 ? <p className="text-sm text-zinc-500">Belum ada data.</p> : items.map(([k, name, s]) => <BreakRow key={k} name={name} s={s} testid={`${testid}-${k}`} />)}
  </Card>
);
